// Firebase Services Initialization (shared app)
import {
  browserLocalPersistence,
  onIdTokenChanged,
  setPersistence,
  signInWithCustomToken,
  signOut,
  type User,
} from "firebase/auth";
import {
  and,
  collection,
  CollectionReference,
  doc,
  type DocumentData,
  DocumentReference,
  type UpdateData,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  or,
  query,
  type QuerySnapshot,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "./firebase";
import type { SupportChannel, LastMessagePreview } from "../../interfaces/Messages/chatTypes";
import {
  TUTOR_CLASS_CHANNELS_COLLECTION,
  TUTOR_CHANNEL_MESSAGES_SUBCOLLECTION,
} from "../../config/Messages/globalConfig";

const TUTOR_SUPPORT_CHANNELS_COLLECTION = "Tutor_Support_Channels" as const;

/** Tutor support message docs often use `timestamp` for ordering (see fetchSupportChannelDataForSidebar); class channels use `createdAt`. */
const messageOrderField = (channelsCollectionName: string): "createdAt" | "timestamp" =>
  channelsCollectionName === TUTOR_SUPPORT_CHANNELS_COLLECTION ? "createdAt" : "createdAt";

const hasMessageTime = (data: Record<string, unknown> | undefined): boolean => {
  if (!data) return false;
  return data.createdAt != null || data.timestamp != null;
};

const mapDocToFirestoreMessage = (
  docId: string,
  data: Record<string, unknown>
): FirestoreMessageDoc => {
  const base = { messageId: docId, ...data } as FirestoreMessageDoc;
  if (!base.createdAt && data.timestamp != null) {
    base.createdAt = data.timestamp as Timestamp;
  }
  return base;
};

/** Support team users collection path: messages_config/support_team/users */
const SUPPORT_TEAM_USERS_COLLECTION_PATH = ["messages_config", "support_team", "users"] as const;

/**
 * Roles config from messages_config/roles_config.
 * categories_config.tutor_support = authorized roles for tutor support channels
 * categories_config.student_support = authorized roles for student support channels
 */
export interface RolesConfig {
  tutor_support: string[];
  student_support: string[];
}

/**
 * Fetch the roles config from messages_config/roles_config.
 * Reads categories_config.tutor_support and categories_config.student_support
 * to get the authorized roles for each channel type.
 */
export const fetchRolesConfig = async (): Promise<RolesConfig | null> => {
  try {
    const rolesDocRef = doc(db, "messages_config", "roles_config");
    const rolesDoc = await getDoc(rolesDocRef);
    if (!rolesDoc.exists()) {
      console.warn("No roles_config document found at messages_config/roles_config");
      return null;
    }
    const data = rolesDoc.data();
    const categoriesConfig = data?.categories_config as Record<string, unknown> | undefined;
    if (!categoriesConfig || typeof categoriesConfig !== "object") {
      console.warn("No categories_config found in roles_config document");
      return null;
    }

    const tutorRoles = categoriesConfig.tutor_support;
    const studentRoles = categoriesConfig.student_support;

    return {
      tutor_support: Array.isArray(tutorRoles) ? tutorRoles : [],
      student_support: Array.isArray(studentRoles) ? studentRoles : [],
    };
  } catch (error: unknown) {
    const err = error as { code?: string };
    console.error("Error fetching roles config:", error);
    if (err?.code === "permission-denied") {
      console.error("Permission denied: reading messages_config/roles_config");
    }
    return null;
  }
};

// Path Builders
const getCollectionRoot = (isSupportChannel: boolean): string =>
  isSupportChannel ? TUTOR_SUPPORT_CHANNELS_COLLECTION : TUTOR_CLASS_CHANNELS_COLLECTION;

const getChatMessagesPath = (
  chatId: string | number,
  messageId: string | null = null,
  isSupportChannel: boolean = false
): CollectionReference | DocumentReference => {
  const collectionRef = collection(db, getCollectionRoot(isSupportChannel), String(chatId), "messages");
  return messageId ? doc(collectionRef, messageId) : collectionRef;
};

export const authTokenListener = (
  userId: string | number | null | undefined,
  onAuthReady: (user: User) => void,
  onUnauthed: () => void
): (() => void) => {
  const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
    if (!firebaseUser) {
      // Not authed; let UI show Google login
      if (typeof onUnauthed === "function") onUnauthed();
      return;
    } else {
      const isForSameUser = firebaseUser.uid === (userId?.toString() ?? "");
      if (!isForSameUser) {
        // Different user; sign out and require login
        await signOut(auth);
        if (typeof onUnauthed === "function") onUnauthed();
        return;
      }
      try {
        const idTokenResult = await firebaseUser.getIdTokenResult();
        console.log("idTokenResult: ", idTokenResult);
        const isTokenValid =
          idTokenResult.expirationTime &&
          new Date() < new Date(idTokenResult.expirationTime);

        if (!isTokenValid) {
          // Invalid token; sign out and require login
          await signOut(auth);
          if (typeof onUnauthed === "function") onUnauthed();
          return;
        }
      } catch (error) {
        console.error("Error while checking firebase token", error);
        if (typeof onUnauthed === "function") onUnauthed();
      }
      // User is authenticated and token is valid
      if (typeof onAuthReady === "function") {
        onAuthReady(firebaseUser);
      }
    }
  });

  return unsubscribe;
};

// Sign-in helpers kept in firestore module
export const signInWithFirebaseCustomToken = async (
  firebaseCustomToken: string
): Promise<void> => {
  if (!firebaseCustomToken) return;
  await setPersistence(auth, browserLocalPersistence);
  await signInWithCustomToken(auth, firebaseCustomToken);
};

interface Student {
  id: string | number;
  name?: string;
  image?: string;
  order?: number;
}

interface Educator {
  id: string | number;
  name?: string;
  image?: string;
  order?: number;
}

/** Raw message doc from Firestore listeners (loose shape for mapping) */
export interface FirestoreMessageDoc {
  [key: string]: unknown;
  messageId?: string;
  createdAt?: Timestamp | null;
  /** Present on some Tutor_Support_Channels messages; copied into createdAt when mapping. */
  timestamp?: Timestamp | null;
}

interface UpdateField {
  fieldName: string;
  value: unknown;
}

interface UploadResult {
  fileUrl: string;
  fileId: string;
}


// Fetch all support team members from Firebase
// Collection: messages_config/support_team/users
export const fetchSupportTeamMembers = async (): Promise<
  Array<Record<string, unknown> & { id: string; code: string }>
> => {
  try {
    const usersCollectionRef = collection(db, ...SUPPORT_TEAM_USERS_COLLECTION_PATH);

    const querySnapshot = await getDocs(usersCollectionRef);

    if (querySnapshot.empty) {
      console.warn(" No users found in messages_config/support_team/users collection");
      return [];
    }

    const users = querySnapshot.docs.map(doc => ({
      id: doc.id,
      code: doc.id,
      ...doc.data()
    }));


    return users;
  } catch (error: unknown) {
    const err = error as { code?: string };
    console.error('Error fetching support team members:', error);
    if (err?.code === 'permission-denied') {
      console.error(' Permission denied: Update Firestore security rules to allow reading messages_config/support_team/users collection');
    }
    return [];
  }
};

/** Single support member doc shape for current user and lazy name resolution */
export interface SupportMemberDoc {
  id: string;
  name: string;
  roles?: string[];
}

/**
 * Subscribe to a single support member document (for current user role updates).
 * Collection: messages_config/support_team/users; document id = userId
 */
export const subscribeSupportMemberById = (
  userId: string | number,
  onUpdate: (data: SupportMemberDoc | null) => void
): (() => void) => {
  try {
    const userDocRef = doc(db, ...SUPPORT_TEAM_USERS_COLLECTION_PATH, String(userId));
    return onSnapshot(
      userDocRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          onUpdate(null);
          return;
        }
        const data = snapshot.data();
        onUpdate({
          id: snapshot.id,
          name: (data?.name as string) ?? "",
          roles: (data?.roles as string[] | undefined) ?? undefined,
        });
      },
      (error: unknown) => {
        const err = error as { code?: string };
        console.error("Subscription error for support member:", error);
        if (err?.code === "permission-denied") onUpdate(null);
      }
    );
  } catch (error) {
    console.error("Error setting up support member subscription:", error);
    return () => {};
  }
};

// Chat Service
export interface ChannelDetails {
  students: Student[];
  educators: Educator[];
  class_type?: "Tutor Support" | "Student Support";
  grade_localization_key?: string;
  /** Subject name on the channel document (for tutor channels) */
  subject_name?: string;
  class_subjects?: { subject_id?: number; subject_name: string }[];
  /** Unread count for support team (Firestore field: supportTeamUnreadMessages) */
  supportTeamUnreadMessages?: number;
}

async function fetchChannelDetailsImpl(
  channelsCollectionName: string,
  chatId: string | number
): Promise<ChannelDetails> {
  try {
    const chatIdStr = String(chatId).trim();
    if (!chatIdStr) {
      console.warn("fetchChannelDetails: chatId is empty, skipping Firestore read");
      return { students: [], educators: [] };
    }
    const channelRef = doc(db, channelsCollectionName, chatIdStr);
    const channelDoc = await getDoc(channelRef);

    if (!channelDoc.exists()) {
      console.warn(`No channel found for chatId: ${chatId}`);
      return { students: [], educators: [] };
    }

    const channelData = channelDoc.data();
    const students = ((channelData?.students as Student[]) || []).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const educators = ((channelData?.educators as Educator[]) || []).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const class_type = channelData?.class_type as "Tutor Support" | "Student Support" | undefined;
    const grade_localization_key = channelData?.grade_localization_key as string | undefined;
    const subject_name = channelData?.subject_name as string | undefined;
    const class_subjects = (channelData?.class_subjects as { subject_id?: number; subject_name: string }[] | undefined) || [];
    const supportTeamUnreadMessages = (channelData?.supportTeamUnreadMessages as number | undefined) ?? 0;
    return { students, educators, class_type, grade_localization_key, subject_name, class_subjects, supportTeamUnreadMessages };
  } catch (error) {
    console.error("Error fetching channel details:", error);
    return { students: [], educators: [] };
  }
}

/** Set supportTeamUnreadMessages to 0 on the channel when the user opens the chat. */
export const resetChannelUnreadCount = async (
  channelsCollectionName: string,
  chatId: string | number
): Promise<void> => {
  try {
    const channelRef = doc(db, channelsCollectionName, String(chatId));
    await updateDoc(channelRef, { supportTeamUnreadMessages: 0 });
  } catch (error) {
    console.error("Error resetting channel unread count:", error);
  }
};

export const chatService = {
  fetchChannelDetails: fetchChannelDetailsImpl,
  resetChannelUnreadCount,
  sendMessage: async (
    chatId: string | number,
    message: Record<string, unknown>,
    isSupportChannel: boolean = false
  ): Promise<void> => {
    try {
      // console.log(`[Chat] Sending message to: ${getCollectionRoot(isSupportChannel)}/${String(chatId)}/messages/${message.messageId}`);
      const messageRef = getChatMessagesPath(
        String(chatId),
        message.messageId as string | null,
        isSupportChannel
      );
      await setDoc(messageRef as DocumentReference, message);
      // console.log("Message sent successfully");
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  },

  subscribeToTargetMessagesById: async (
    channelsCollectionName: string,
    chatId: string | number,
    _userId: string | number,
    callback: (messages: FirestoreMessageDoc[], limit: number) => void,
    targetMessageId: string | null = null,
    limitStep: number = 50
  ): Promise<() => void> => {
    try {
      const messagesRef = collection(
        db,
        channelsCollectionName,
        String(chatId),
        TUTOR_CHANNEL_MESSAGES_SUBCOLLECTION
      );

      let found = false;
      let currentLimit = limitStep;
      let initialMessages: FirestoreMessageDoc[] = [];
      const orderField = messageOrderField(channelsCollectionName);

      // Keep increasing limit until targetMessageId is found or we exhaust
      while (!found) {
        const messagesQuery = query(
          messagesRef,
          orderBy(orderField, "desc"),
          limit(currentLimit)
        );

        const snapshot = await getDocs(messagesQuery);

        initialMessages = snapshot.docs
          .map((d) => mapDocToFirestoreMessage(d.id, d.data() as Record<string, unknown>))
          .filter((msg: FirestoreMessageDoc) => hasMessageTime(msg as Record<string, unknown>));

        found =
          initialMessages.some((m) => m.messageId === targetMessageId) ||
          targetMessageId == null;

        if (!found && snapshot.size >= currentLimit) {
          // Increase limit and retry
          currentLimit += limitStep;
        } else {
          break; // either found or no more data
        }
      }

      if (initialMessages.length > 0) {
        callback(initialMessages.reverse(), currentLimit); // oldest → newest
      }

      // ✅ Now start the listener from the latest query
      const finalQuery = query(
        messagesRef,
        orderBy(orderField, "desc"),
        limit(currentLimit)
      );



      let firstSnapshotSkipped = false;
      const unsubscribe = onSnapshot(finalQuery, (snapshot) => {
        if (!firstSnapshotSkipped) {
          firstSnapshotSkipped = true;
          return; // skip duplicate initial snapshot
        }

        const newMessages = snapshot
          .docChanges()
          .filter(
            (change) =>
              change.type !== "removed" && hasMessageTime(change.doc.data() as Record<string, unknown>)
          )
          .map((change) => ({
            ...mapDocToFirestoreMessage(change.doc.id, change.doc.data() as Record<string, unknown>),
            changeType: change.type,
          }));

        if (newMessages.length > 0) {
          callback(newMessages.reverse(), currentLimit); // oldest → newest
        }
      });

      return () => {
        unsubscribe();
      };
    } catch (error) {
      console.error("Error subscribing to chat messages:", error);
      return () => { };
    }
  },
  subscribeToMessagesById: async (
    channelsCollectionName: string,
    chatId: string | number,
    _userId: string | number,
    callback: (messages: FirestoreMessageDoc[]) => void,
    limitVariable: number = 50
  ): Promise<() => void> => {
    try {
      // Check if user has this channel
      //const channelsSnapshot = await queryUserChannels(channelsCollectionName, userId);
      //const targetChannel = channelsSnapshot.docs.find(doc => doc.data().id === chatId);
      //if (!targetChannel) {
      //    console.warn(`No channel found for chatId: ${chatId}`);
      //    return () => { };
      //}

      const messagesRef = collection(
        db,
        channelsCollectionName,
        String(chatId),
        TUTOR_CHANNEL_MESSAGES_SUBCOLLECTION
      );
      const orderField = messageOrderField(channelsCollectionName);
      const messagesQuery = query(
        messagesRef,
        orderBy(orderField, "desc"),
        limit(limitVariable)
      );

      // STEP 1 — Initial one-time load
      const initialSnapshot = await getDocs(messagesQuery);
      const initialMessages = initialSnapshot.docs
        .map((doc) => mapDocToFirestoreMessage(doc.id, doc.data() as Record<string, unknown>))
        .filter((msg: FirestoreMessageDoc) => hasMessageTime(msg as Record<string, unknown>));

      // Call callback once with the initial messages
      if (initialMessages.length > 0) {
        callback(initialMessages.reverse()); // oldest → newest
      }



      // STEP 2 — Live listener for new messages
      let firstSnapshotSkipped = false;

      const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
        if (!firstSnapshotSkipped) {
          // Skip the first snapshot (same as initial load)
          firstSnapshotSkipped = true;
          return;
        }
        // Only process newly added or modified messages
        const newMessages = snapshot
          .docChanges()
          .filter(
            (change) =>
              change.type != "removed" &&
              hasMessageTime(change.doc.data() as Record<string, unknown>)
          )
          .map((change) => ({
            ...mapDocToFirestoreMessage(change.doc.id, change.doc.data() as Record<string, unknown>),
            changeType: change.type,
          }));

        if (newMessages.length > 0) {
          callback(newMessages.reverse()); // Keep ordering consistent
        }
      });

      return () => {
        unsubscribe();
      };
    } catch (error) {
      console.error("Error subscribing to chat messages:", error);
      return () => { };
    }
  },
  updateMessageReactions: async (
    channelsCollectionName: string,
    chatId: string | number,
    messageId: string,
    reactions: unknown
  ): Promise<void> => {
    try {
      const isSupportChannel = channelsCollectionName === TUTOR_SUPPORT_CHANNELS_COLLECTION;
      const messageRef = getChatMessagesPath(
        String(chatId),
        messageId,
        isSupportChannel
      ) as DocumentReference;
      await updateDoc(messageRef, { reactions });

    } catch (error) {
      console.error("Error updating message reactions:", error);
      throw error;
    }
  },
  updateStatus: async (
    channelsCollectionName: string,
    chatId: string | number,
    messageId: string,
    updates: UpdateField[]
  ): Promise<void> => {
    try {
      const isSupportChannel = channelsCollectionName === TUTOR_SUPPORT_CHANNELS_COLLECTION;
      const messageRef = getChatMessagesPath(
        String(chatId),
        messageId,
        isSupportChannel
      ) as DocumentReference;

      // Convert array of { fieldName, value } into object
      const updateData = updates.reduce(
        (acc: Record<string, unknown>, { fieldName, value }) => {
          acc[fieldName] = value;
          return acc;
        },
        {}
      );

      await updateDoc(messageRef, updateData as UpdateData<DocumentData>);

    } catch (error) {
      console.error("Error updating message status:", error);
      throw error;
    }
  },
  uploadToFirebaseStorage: async (
    chatId: string | number,
    file: Blob | File
  ): Promise<UploadResult | null> => {
    try {
      const fileId = crypto.randomUUID();
      const storageRef = ref(storage, `${chatId}/messages/${fileId}`);
      const uploadTask = await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(uploadTask.ref);

      return { fileUrl, fileId };
    } catch (error) {
      console.error("Error uploading file:", error);
      return null;
    }
  },
};


export interface SubscribeToStudentChannelsOptions {
  gradeIds?: number[];
  searchStudentId?: string;
}
export interface SubscribeToTutorChannelsOptions {
  searchName?: string;
}

export interface SubscribeToChannelsWithLimitOptions {
  useStudentFilters?: boolean;
  gradeIds?: number[];
  searchStudentId?: string;
  searchName?: string;
}

const mapSnapshotToChannels = (
  snapshot: QuerySnapshot<unknown, DocumentData>
): SupportChannel[] =>
  snapshot.docs.map((d) => {
    const data = (d.data() ?? {}) as Record<string, unknown>;
    const supportTeamUnread = data.supportTeamUnreadMessages as number | undefined;
    const unreadCount = supportTeamUnread ?? (data.unreadCount as number | undefined) ?? 0;
    return {
      id: d.id,
      ...data,
      unreadCount,
    } as SupportChannel;
  });

/** Priority for tutor search: 0 = first_name, 1 = last_name, 2 = subject_name. Lower first. */
function getTutorSearchMatchPriority(
  channel: SupportChannel,
  searchTerm: string
): number {
  const term = searchTerm.toLowerCase().trim();
  if (!term) return 2;
  const first = ((channel as { first_name?: string }).first_name ?? "").toLowerCase();
  const last = ((channel as { last_name?: string }).last_name ?? "").toLowerCase();
  const subj = ((channel as { subject_name?: string }).subject_name ?? "").toLowerCase();
  if (first.startsWith(term)) return 0;
  if (last.startsWith(term)) return 1;
  if (subj.startsWith(term)) return 2;
  return 2; // no clear match (e.g. match was on a different field), put last
}

/** Sort tutor search results: first_name matches first, then last_name, then subject_name; within each group by createdAt desc. */
function sortTutorChannelsBySearchPriority(
  channels: SupportChannel[],
  searchTerm: string
): SupportChannel[] {
  return [...channels].sort((a, b) => {
    const pa = getTutorSearchMatchPriority(a, searchTerm);
    const pb = getTutorSearchMatchPriority(b, searchTerm);
    if (pa !== pb) return pa - pb;
    const aSec = (a as { createdAt?: Timestamp }).createdAt?.seconds ?? 0;
    const bSec = (b as { createdAt?: Timestamp }).createdAt?.seconds ?? 0;
    return bSec - aSec;
  });
}

const runChannelSnapshot = (
  q: ReturnType<typeof query>,
  callback: (channels: SupportChannel[]) => void,
  indexHint: string
) =>
  onSnapshot(
    q,
    (snapshot) => callback(mapSnapshotToChannels(snapshot)),
    (error: unknown) => {
      const err = error as { code?: string; message?: string };
      console.error(" [CHANNELS WITH LIMIT] Error:", error);
      if (err?.code === "failed-precondition" && err?.message?.includes("index")) {
        console.error(` Create the required Firestore composite index. ${indexHint}`);
      }
      callback([]);
    }
  );

// --- Tutor messages sidebar: educator API classes + Firestore channel docs ---

const coerceFirestoreTimestamp = (raw: unknown): Timestamp | null => {
  if (raw instanceof Timestamp) return raw;
  if (raw && typeof raw === "object" && "seconds" in raw) {
    const o = raw as { seconds: number; nanoseconds?: number };
    return new Timestamp(o.seconds, o.nanoseconds ?? 0);
  }
  return null;
};

/** Channel doc shape under {@link TUTOR_CLASS_CHANNELS_COLLECTION} (doc id = class_id). */
export interface TutorClassChannelDoc {
  id: string;
  classId: number;
  lastMessage?: LastMessagePreview;
  unreadCounts: Record<string, number>;
}

export const fetchSupportChannelDataForSidebar = async (
  userId: string
): Promise<{
  lastMessage: LastMessagePreview | null;
  unreadMessagesCount: number;
}> => {
  const userIdStr = String(userId);
  let lastMessage: LastMessagePreview | null = null;
  let unreadMessagesCount = 0;

  try {
    const channelDocRef = doc(db, TUTOR_SUPPORT_CHANNELS_COLLECTION, userIdStr);
    const channelSnap = await getDoc(channelDocRef);
    if (channelSnap.exists()) {
      const channelData = channelSnap.data();
      const rawLastMessage = channelData?.lastMessage as Record<string, unknown> | undefined;
      if (rawLastMessage) {
        lastMessage = {
          serverTimestamp: coerceFirestoreTimestamp(rawLastMessage.serverTimestamp as Timestamp) ?? undefined,
          content: rawLastMessage.content as string | undefined,
          type: rawLastMessage.type as number | undefined,
          senderName: rawLastMessage.senderName as string | undefined,
          senderId: rawLastMessage.senderId as string | number | undefined,
          senderType: rawLastMessage.senderType as string | undefined,
          status: rawLastMessage.status as number | undefined,
        };
      }
    }
  } catch (error) {
    console.error("[SupportChannel] Error reading channel doc:", error);
  }

  try {
    const unreadRef = doc(
      db,
      TUTOR_SUPPORT_CHANNELS_COLLECTION,
      userIdStr,
      "unreadMessages",
      userIdStr
    );
    const unreadSnap = await getDoc(unreadRef);
    unreadMessagesCount = unreadSnap.exists() ? ((unreadSnap.data().count as number) || 0) : 0;
  } catch (error) {
    console.error("[SupportChannel] Error reading unread count:", error);
  }

  return { lastMessage, unreadMessagesCount };
};

export const subscribeToSupportChannelForSidebar = (
  userId: string,
  callback: (data: {
    unreadMessagesCount: number;
    lastMessage: LastMessagePreview | null;
  }) => void
): (() => void) => {
  const userIdStr = String(userId);
  const channelRef = doc(db, TUTOR_SUPPORT_CHANNELS_COLLECTION, userIdStr);

  const channelUnsub = onSnapshot(
    channelRef,
    (snapshot) => {
      let lastMessage: LastMessagePreview | null = null;
      if (snapshot.exists()) {
        const channelData = snapshot.data();
        const rawLastMessage = channelData?.lastMessage as Record<string, unknown> | undefined;
        if (rawLastMessage) {
          lastMessage = {
            serverTimestamp: coerceFirestoreTimestamp(rawLastMessage.serverTimestamp as Timestamp) ?? undefined,
            content: rawLastMessage.content as string | undefined,
            type: rawLastMessage.type as number | undefined,
            senderName: rawLastMessage.senderName as string | undefined,
            senderId: rawLastMessage.senderId as string | number | undefined,
            senderType: rawLastMessage.senderType as string | undefined,
            status: rawLastMessage.status as number | undefined,
          };
        }
      }
      callback({ unreadMessagesCount: 0, lastMessage });
    },
    (error: unknown) => {
      console.error("Error subscribing to tutor support channel (sidebar):", error);
      callback({ unreadMessagesCount: 0, lastMessage: null });
    }
  );

  const unreadRef = doc(
    db,
    TUTOR_SUPPORT_CHANNELS_COLLECTION,
    userIdStr,
    "unreadMessages",
    userIdStr
  );
  const unreadUnsub = onSnapshot(
    unreadRef,
    (docSnapshot) => {
      const unreadMessagesCount = docSnapshot.exists()
        ? ((docSnapshot.data().count as number) || 0)
        : 0;
      callback({ unreadMessagesCount, lastMessage: null });
    },
    (error: unknown) => {
      console.error("Error subscribing to support unread count:", error);
    }
  );

  return () => {
    channelUnsub();
    unreadUnsub();
  };
};

export const fetchChannelsByClassIds = async (
  classIds: number[]
): Promise<Record<number, TutorClassChannelDoc>> => {
  const channels: Record<number, TutorClassChannelDoc> = {};
  try {
    await Promise.all(
      classIds.map(async (classId) => {
        const channelRef = doc(db, TUTOR_CLASS_CHANNELS_COLLECTION, String(classId));
        const channelSnap = await getDoc(channelRef);
        if (!channelSnap.exists()) return;

        const data = channelSnap.data();
        const rawLastMessage = data.lastMessage as Record<string, unknown> | undefined;
        const lastMessage: LastMessagePreview | undefined = rawLastMessage
          ? {
              serverTimestamp: coerceFirestoreTimestamp(rawLastMessage.serverTimestamp as Timestamp) ?? undefined,
              content: rawLastMessage.content as string | undefined,
              type: rawLastMessage.type as number | undefined,
              senderName: rawLastMessage.senderName as string | undefined,
              senderId: rawLastMessage.senderId as string | number | undefined,
              senderType: rawLastMessage.senderType as string | undefined,
              status: rawLastMessage.status as number | undefined,
            }
          : undefined;
        channels[classId] = {
          id: channelSnap.id,
          classId,
          lastMessage,
          unreadCounts: (data.unreadCounts as Record<string, number>) || {},
        };
      })
    );
    return channels;
  } catch (error) {
    console.error("Error fetching channels by class IDs:", error);
    throw error;
  }
};

export const subscribeToClassChannelForSidebar = (
  classId: number,
  callback: (channelDoc: TutorClassChannelDoc | null) => void
): (() => void) => {
  const channelRef = doc(db, TUTOR_CLASS_CHANNELS_COLLECTION, String(classId));

  return onSnapshot(
    channelRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }
      const data = snapshot.data();
      const rawLastMessage = data.lastMessage as Record<string, unknown> | undefined;
      const lastMessage: LastMessagePreview | undefined = rawLastMessage
        ? {
            serverTimestamp: coerceFirestoreTimestamp(rawLastMessage.serverTimestamp as Timestamp) ?? undefined,
            content: rawLastMessage.content as string | undefined,
            type: rawLastMessage.type as number | undefined,
            senderName: rawLastMessage.senderName as string | undefined,
            senderId: rawLastMessage.senderId as string | number | undefined,
            senderType: rawLastMessage.senderType as string | undefined,
            status: rawLastMessage.status as number | undefined,
          }
        : undefined;
      callback({
        id: snapshot.id,
        classId,
        lastMessage,
        unreadCounts: (data.unreadCounts as Record<string, number>) || {},
      });
    },
    (error: unknown) => {
      console.error("Error subscribing to class channel (sidebar):", error);
      callback(null);
    }
  );
};

/** Clears the current user's unread entry on the channel document (tutor support or class channel). */
export const resetTutorChannelUnreadForUser = async (
  chatDocId: string | number,
  userId: string,
  isSupportChannel: boolean
): Promise<void> => {
  const uid = String(userId);
  const collectionName = isSupportChannel
    ? TUTOR_SUPPORT_CHANNELS_COLLECTION
    : TUTOR_CLASS_CHANNELS_COLLECTION;
  try {
    const channelRef = doc(db, collectionName, String(chatDocId));
    await updateDoc(channelRef, {
      [`unreadCounts.${uid}`]: 0,
    });
  } catch (error) {
    console.error("Error resetting tutor channel unread count:", error);
  }
};

